import React from 'react';

/**
 * PlayersHudPanel Component
 * Sidebar panel showing detailed player information
 */
export default function PlayersHudPanel({ 
  allSeats, 
  gameState, 
  localPlayerID,
  creditValue,
  hands = []
}) {
  const credits = gameState.credits || {};
  const activePlayerIdx = gameState.activePlayerIdx;
  const knocked = gameState.knocked || [];
  const discarded = gameState.discarded || [];
  const readyPlayers = gameState.readyPlayers || [];
  const suitVotes = gameState.suitVotes || [];
  const dealerIdx = gameState.dealerIdx;
  const phase = gameState.phase;
  
  return (
    <div
      style={{
        width: '280px',
        background: '#ffffff',
        borderRadius: '12px',
        padding: '11px', // Reduced by 30% (16px * 0.7)
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
        border: '1px solid #e2e8f0',
        overflow: 'visible' // No scrolling - expand to fit content
      }}
    >
      <h3
        style={{
          margin: '0 0 12px 0',
          fontSize: '0.9rem', // Increased from 0.7875rem
          fontWeight: '700',
          color: '#1e293b',
          borderBottom: '2px solid #e2e8f0',
          paddingBottom: '8px'
        }}
      >
        Players
      </h3>
      
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: '1fr 1fr', 
        gap: '8px' // Increased gap slightly
      }}>
        {allSeats.map((player, idx) => {
          const isLocalPlayer = player.playerID === localPlayerID;
          const isActive = activePlayerIdx === idx;
          const hasKnocked = knocked.includes(idx);
          const hasDiscarded = discarded.includes(idx);
          const isReady = readyPlayers.includes(player.playerID);
          const hasVoted = suitVotes.some(v => v.playerID === player.playerID);
          const isDealer = dealerIdx === idx;
          const playerCredits = credits[player.playerID] || 0;
          
          return (
            <div
              key={player.playerID}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'stretch',
                gap: '5px',
                padding: '7px',
                borderRadius: '8px',
                background: isActive ? '#eff6ff' : '#f8fafc',
                border: isActive ? '2px solid #3b82f6' : '1px solid #e2e8f0',
                transition: 'all 0.2s ease',
                minWidth: 0, // Allow shrinking in grid
                minHeight: 'fit-content', // Ensure content isn't cut off
                overflow: 'visible' // Allow content to be fully visible
              }}
            >
              {/* Avatar and name row */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', minHeight: '26px' }}>
                <img
                  src={player.avatarUrl || '/avatars/avatar1.png'}
                  alt={player.name}
                  style={{
                    width: '26px',
                    height: '26px',
                    borderRadius: '50%',
                    border: `2px solid ${isLocalPlayer ? '#3b82f6' : player.isBot ? '#10b981' : '#64748b'}`,
                    flexShrink: 0
                  }}
                />
                <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: '3px', flexWrap: 'wrap' }}>
                  <span
                    style={{
                      fontWeight: '600',
                      fontSize: '0.65rem', // Increased from 0.55rem
                      color: '#1e293b',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      flex: 1,
                      minWidth: 0
                    }}
                  >
                    {player.name}
                  </span>
                  {isLocalPlayer && (
                    <span style={{ fontSize: '0.55rem', color: '#3b82f6' }}>(You)</span>
                  )}
                  {player.isBot && (
                    <span style={{ fontSize: '0.55rem', color: '#10b981' }}>🤖</span>
                  )}
                  {isDealer && (
                    <span style={{ fontSize: '0.55rem', color: '#fbbf24' }}>👑</span>
                  )}
                </div>
              </div>
              
              {/* Status indicators */}
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '3px',
                  marginBottom: '3px',
                  minHeight: 'fit-content'
                }}
              >
                  {phase === 'waiting' && isReady && (
                    <Badge label="Ready" color="#10b981" />
                  )}
                  {phase === 'suitvote' && hasVoted && (
                    <>
                      <Badge label="Voted" color="#3b82f6" />
                      {(() => {
                        const votedSuit = suitVotes.find(v => v.playerID === player.playerID)?.suit;
                        const suitSymbols = { spades: "♠", hearts: "♥", diamonds: "♦", clubs: "♣" };
                        return votedSuit ? (
                          <div style={{ fontSize: '0.5rem', color: '#3b82f6', fontWeight: '600', marginTop: '2px' }}>
                            Suit selection: <span style={{ fontSize: '0.55rem' }}>{suitSymbols[votedSuit] || votedSuit}</span>
                          </div>
                        ) : null;
                      })()}
                    </>
                  )}
                  {phase === 'action' && isActive && (
                    <Badge label="Thinking..." color="#f59e0b" />
                  )}
                  {hasKnocked && (
                    <Badge label="Knocked" color="#10b981" />
                  )}
                  {hasDiscarded && (
                    <Badge label="Discarded" color="#ef4444" />
                  )}
                </div>
                
              {/* Cards display - show during action, reveal, and results phases */}
              {((phase === 'action' || phase === 'reveal' || phase === 'results') && hands && hands[idx] && hands[idx].length > 0) && (
                <div
                  style={{
                    fontSize: '0.55rem',
                    color: '#1e293b',
                    fontWeight: '600',
                    lineHeight: '1.4',
                    marginBottom: '3px'
                  }}
                >
                  {(() => {
                    const playerHand = hands[idx];
                    const suitSymbols = { spades: "♠", hearts: "♥", diamonds: "♦", clubs: "♣" };
                    const suitColors = { spades: "#333", clubs: "#333", hearts: "#d22", diamonds: "#d22" };
                    const showCards = isLocalPlayer || phase === 'reveal' || phase === 'results' || hasKnocked;
                    
                    return playerHand.map((card, cardIdx) => {
                      if (!showCards && !isLocalPlayer) {
                        // Show face-down indicator for opponents during action phase
                        return <span key={cardIdx} style={{ marginRight: '4px' }}>🎴</span>;
                      }
                      return (
                        <span 
                          key={cardIdx}
                          style={{
                            color: suitColors[card.suit] || '#333',
                            marginRight: '4px',
                            display: 'inline-block'
                          }}
                        >
                          {card.rank}{suitSymbols[card.suit] || card.suit}
                        </span>
                      );
                    });
                  })()}
                </div>
              )}
              
              {/* Credits */}
              <div
                style={{
                  fontSize: '0.55rem', // Increased from 0.45rem
                  color: '#64748b',
                  fontWeight: '500',
                  lineHeight: '1.3'
                }}
              >
                ${(playerCredits * creditValue).toFixed(2)} ({playerCredits} chips)
              </div>
            </div>
          );
        })}
        {/* Add blank placeholder if odd number of players */}
        {allSeats.length % 2 === 1 && (
          <div style={{ 
            display: 'flex',
            flexDirection: 'column',
            padding: '6px',
            borderRadius: '8px',
            background: 'transparent',
            border: '1px solid transparent',
            visibility: 'hidden' // Invisible but takes up space
          }}>
            <div style={{ display: 'flex', gap: '6px' }}>
              <div style={{ width: '24px', height: '24px' }}></div>
              <div style={{ flex: 1 }}></div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Badge({ label, color }) {
  return (
    <span
      style={{
        background: `${color}20`,
        color: color,
        padding: '2px 5px',
        borderRadius: '3px',
        fontSize: '0.55rem', // Increased from 0.455rem
        fontWeight: '600',
        lineHeight: '1.2'
      }}
    >
      {label}
    </span>
  );
}

